import { useQueryClient } from '@tanstack/react-query'
import {
  CountryCode,
  CurrencyCode,
  encodeToken,
  formatCurrency,
  getMarketProfileUpdateData,
  hoverArrow,
  IMarketPerson,
  IMarketProduct,
  IMarketProfile,
  INewMarketPerson,
  INewMarketProduct,
  LaunchpadView,
  TranslatedText
} from 'core'
import { compact, isEqual } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { FormGroup, Input, Tooltip } from '~/components'
import {
  marketStateQueryKey,
  useCreateStripeAccountLoginLink,
  useCreateStripeAccountOnboardingLink,
  usePollMarketState,
  useUpdateMarketProfile,
  useUpdateProfileAvatar
} from '~/hooks'
import {
  breakpoint,
  Button,
  Icon,
  Link,
  Loader,
  openURL,
  Pressable,
  Select,
  styled,
  Text,
  toast,
  useHistory,
  useLoadUserData,
  useStore,
  useTranslation,
  View
} from '~/lite'

import { AccountDialog } from './AccountDialog'
import { IntegrationDialog, IntegrationType } from './IntegrationDialog'
import { PersonDialog } from './PersonDialog'
import { ProductDialog } from './ProductDialog'
import { Profile } from './profile/Profile'
import { useOptions } from './useOptions'

const Wrap = styled(View)`
  flex: 1;
  position: fixed;
  top: 60px;
  right: 0;
  bottom: 0;
  left: 0;
  flex-direction: row;
  justify-content: stretch;
  align-items: stretch;
  overflow: hidden;
  background: ${props => props.theme.background};
`

const Main = styled(View)`
  position: relative;
  flex: 1;
  align-items: stretch;
  margin-top: 64px;

  @media ${breakpoint.md} {
    margin-top: 0;
  }
`

const MainBody = styled(View)`
  flex: 1;
  overflow-y: auto;
  box-shadow: 15px 0 30px 0 rgba(0, 0, 0, 1);

  &::-webkit-scrollbar-thumb {
    visibility: hidden;
  }

  &:hover::-webkit-scrollbar-thumb {
    visibility: visible;
  }
`

const Sidebar = styled(View)<{ $visible: boolean }>`
  position: absolute;
  top: 64px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  justify-content: stretch;
  align-items: stretch;
  background: ${props => props.theme.backgroundContrast};
  transition: width 0.4s ease-in-out;
  will-change: width;
  overflow: hidden;
  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};

  @media ${breakpoint.md} {
    position: static;
    display: flex;
    visibility: visible;
    z-index: initial;
    width: ${props => (props.$visible ? '500px' : 0)};
  }
`

const SidebarInner = styled(View)`
  flex: 1;
  justify-content: stretch;
  align-items: stretch;
  width: 100%;

  @media ${breakpoint.md} {
    width: 500px;
  }
`

const SidebarBody = styled(View)`
  flex: 1;
  align-items: stretch;
  padding: 16px;
  gap: 16px;
  overflow-y: auto;

  &::-webkit-scrollbar-thumb {
    visibility: hidden;
  }

  &:hover::-webkit-scrollbar-thumb {
    visibility: visible;
  }
`

const SidebarMenu = styled(View)`
  padding: 16px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  border-bottom-width: 1px;
  gap: 16px;
  background: ${props => props.theme.background};
  z-index: 1;
`

const SidebarFooter = styled(View)`
  flex-direction: row;
  padding: 16px;
  border-top-width: 1px;
  background: ${props => props.theme.background};
  z-index: 1;
`

const SidebarButton = styled(Button)`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};

  &:hover {
    background: ${props => props.theme.border};
  }
`

const List = styled(View)`
  align-items: stretch;
  justify-content: center;
  border-width: 1px;
  border-radius: 12px;
  background: ${props => props.theme.cardBackground};
`

const ListItem = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 8px;
  padding: 16px;
  transition: border-color 0.2s ease-in-out;
  border-top-width: 1px;

  &:first-child {
    border-top-width: 0;
  }
`

const ListItemLabel = styled(Text)`
  flex: 1;
  text-wrap: balance;
  font-size: 16px;
`

const Arrow = styled.span``

const GeneralMenuItem = styled(View)`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 4px 16px;
  border-width: 1px;
  background: ${props => (props.theme.dark ? props.theme.background : props.theme.cardBackground)};
  border-radius: 10px;
`

const CofounderMenuItem = styled(GeneralMenuItem)`
  border-bottom-width: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`

const MenuItemText = styled(Text)`
  flex: 1;
  font-size: 16px;
`

const MenuItemSecondaryText = styled(Text)`
  font-size: 16px;
  color: ${props => props.theme.text};
  opacity: 0.5;
`

const MenuItemLink = styled(Link)`
  font-size: 16px;
  color: ${props => props.theme.text};
  opacity: 0.5;

  &:hover {
    text-decoration: underline;
  }
`

const MenuItemInput = styled(Input)`
  padding-top: 7px;
  padding-bottom: 7px;
`

const UsernameInput = styled(Input)`
  padding: 7px 0 7px 16px;
  text-align: right;
`

const ToggleSidebarButton = styled(Button)`
  position: absolute;
  top: 14px;
  left: 14px;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 100px;
  z-index: 100;
  opacity: 0.5;
  visibility: hidden;

  &:hover {
    opacity: 1;
  }

  @media ${breakpoint.md} {
    visibility: visible;
  }
`

const DashboardMenu = styled(View)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 101;
  flex-direction: row;
  align-items: center;
  justify-content: stretch;
  flex-wrap: wrap;
  padding: 0 16px;
  height: 64px;
  border-bottom-width: 1px;
  background: ${props => props.theme.background};

  @media ${breakpoint.md} {
    visibility: hidden;
  }
`

const QuestionList = styled(View)`
  gap: 16px;
`

const Question = styled(View)`
  flex-direction: row;
  align-items: start;
`

const ListWrap = styled(View)`
  width: 100%;
  gap: 8px;
`

const Deliverable = styled(View)`
  flex-direction: row;
  align-items: center;
`

const DeleteButton = styled(Button)`
  background: none;
  padding: 0;
  height: 40px;
  width: 40px;
  opacity: 0.5;

  &:hover {
    background: none;
    opacity: 1;
  }
`

const defaultProfile: Omit<IMarketProfile, 'id' | 'createdAt' | 'updatedAt'> = {
  companyName: null,
  username: '' as IMarketProfile['username'],
  avatar: null,
  businessType: 'Individual',
  metadata: {},
  country: 'US' as CountryCode,
  secondsToGenerate: null,
  hourlyRate: 50,
  currency: 'USD' as CurrencyCode,
  affiliateRate: 10,
  cofounderState: 'Open',
  cofounderNote: null,
  calendarBookingUrl: null,
  memberships: [],
  people: [],
  products: [],
  setupVersion: null,
  state: 'Draft'
}

export interface ILaunchpadDashboardProps {
  marketProfileId: IMarketProfile['id']
  view?: LaunchpadView
}

export const LaunchpadDashboard: React.FC<ILaunchpadDashboardProps> = ({ marketProfileId, view }) => {
  const t = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()

  const [searchParams] = useSearchParams()
  const isStripeCallback = useMemo(() => searchParams.get('action') === 'return', [searchParams])

  const theme = useStore(state => state.theme)
  const locale = useStore(state => state.locale)
  const isLoadingUser = useStore(state => state.isLoadingUser)

  const [activeIntegration, setActiveIntegration] = useState<IntegrationType | null>(null)
  const [activePerson, setActivePerson] = useState<IMarketPerson | INewMarketPerson | null>(null)
  const [activeProduct, setActiveProduct] = useState<IMarketProduct | INewMarketProduct | null>(null)
  const [isEditingUsername, setIsEditingUsername] = useState(false)
  const [isEditingHourlyRate, setIsEditingHourlyRate] = useState(false)
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false)
  const [isIntegrationDialogOpen, setIsIntegrationDialogOpen] = useState(false)
  const [isPersonDialogOpen, setIsPersonDialogOpen] = useState(false)
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)

  const options = useOptions()
  const loadUserData = useLoadUserData()

  const {
    control,
    watch,
    formState: { errors },
    reset
  } = useForm<IMarketProfile>({ defaultValues: defaultProfile })

  // for records with ids, useFieldArray will override our id with its own auto-generated id unless we specify keyName
  const people = useFieldArray({ control, name: 'people', keyName: 'key' })
  const products = useFieldArray({ control, name: 'products', keyName: 'key' })
  const deliverables = useFieldArray({ control, name: 'metadata.deliverables.items', keyName: 'key' })
  const testimonials = useFieldArray({ control, name: 'metadata.testimonials.items', keyName: 'key' })
  const faq = useFieldArray({ control, name: 'metadata.faq.questions', keyName: 'key' })

  const profile = watch()

  const resetForm = (profile: IMarketProfile) => {
    reset(profile)

    // useFieldArray doesn't seem to update when useForm reset is called, so we need to manually update it
    people.replace((profile.people as any) ?? [])
    products.replace((profile.products as any) ?? [])
    deliverables.replace((profile.metadata.deliverables?.items as any) ?? [])
    testimonials.replace((profile.metadata.testimonials?.items as any) ?? [])
    faq.replace((profile.metadata.faq?.questions as any) ?? [])
  }

  // need to poll the market state when we initially generate the profile
  const {
    data: loadedMarketState,
    refetch: refetchMarketState,
    isLoading: isLoadingMarketState
  } = usePollMarketState(marketProfileId, {
    refreshStripe: isStripeCallback ? marketProfileId : undefined,
    onSuccess: data => {
      if (isStripeCallback) {
        history.replace(`/launchpad/${encodeToken(marketProfileId)}/payments`)
      }

      const profile = data?.profiles.find(profile => profile.id === marketProfileId)

      if (profile) {
        resetForm(profile)
      }
    }
  })

  const { mutateAsync: updateMarketProfile, isLoading: isUpdatingProfile } = useUpdateMarketProfile()

  const { mutateAsync: createStripeAccountOnboardingLink, isLoading: isCreatingAccountOnboardingLink } =
    useCreateStripeAccountOnboardingLink()

  const { mutateAsync: createStripeAccountLoginLink, isLoading: isCreatingAccountLoginLink } =
    useCreateStripeAccountLoginLink()

  if (isStripeCallback && !isLoadingMarketState) {
    refetchMarketState()
  }

  const loadedProfile = loadedMarketState?.profiles.find(profile => profile.id === marketProfileId)

  // profile seems to easily get reset and lose its state, so we need to rehydrate it from the loadedMarketState
  if (!profile.id && loadedProfile) {
    resetForm(loadedProfile)
  }

  const { mutateAsync: updateProfileAvatar, isLoading: isUpdatingProfileAvatar } = useUpdateProfileAvatar({
    onSuccess: () => {
      loadUserData()
      // queryClient.invalidateQueries(['user-profile', username])
      // queryClient.invalidateQueries(['user-resume', username])
    }
  })

  useEffect(() => {
    document.body.classList.add('profile-editor-view')
    return () => document.body.classList.remove('profile-editor-view')
  }, [])

  const changes = useMemo(() => {
    if (!loadedProfile || !profile) {
      return null
    }

    const localData = getMarketProfileUpdateData(profile)
    const loadedData = getMarketProfileUpdateData(loadedProfile)

    return isEqual(localData, loadedData) ? null : localData
  }, [profile, loadedProfile])

  const renderHero = () => (
    <>
      <Controller
        control={control}
        name="metadata.hero.header"
        render={({ field }) => (
          <FormGroup label="Header" helperText={errors.metadata?.hero?.header?.message} style={{ marginBottom: 0 }}>
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.hero.subheader"
        render={({ field }) => (
          <FormGroup
            label="Subheader"
            helperText={errors.metadata?.hero?.subheader?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.hero.cta"
        render={({ field }) => (
          <FormGroup
            label="Call to action"
            helperText={errors.metadata?.hero?.cta?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
    </>
  )

  const renderDeliverables = () => (
    <>
      <Controller
        control={control}
        name="metadata.deliverables.header"
        render={({ field }) => (
          <FormGroup
            label="Header"
            helperText={errors.metadata?.deliverables?.header?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.deliverables.subheader"
        render={({ field }) => (
          <FormGroup
            label="Subheader"
            helperText={errors.metadata?.deliverables?.subheader?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <ListWrap>
        <Text marginX="xs" text="Deliverables" />
        {deliverables.fields.map((deliverable, index) => (
          <Controller
            key={deliverable.key}
            control={control}
            name={`metadata.deliverables.items.${index}.name`}
            render={({ field }) => (
              <Deliverable>
                <Input
                  {...field}
                  wrapStyle={{ flex: 1, backgroundColor: theme.background, borderColor: theme.border }}
                />
                <DeleteButton icon="trash" onPress={() => deliverables.remove(index)} skipTracking />
              </Deliverable>
            )}
          />
        ))}
        <View flexDirection="row">
          <SidebarButton
            icon="add"
            text="Add"
            onPress={() => deliverables.append({ name: '' as TranslatedText })}
            skipTracking={true as any}
            action={undefined as any}
          />
          <View flex={1} />
        </View>
      </ListWrap>
    </>
  )

  const renderTestimonials = () => (
    <>
      <Controller
        control={control}
        name="metadata.testimonials.header"
        render={({ field }) => (
          <FormGroup
            label="Header"
            helperText={errors.metadata?.testimonials?.header?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.testimonials.subheader"
        render={({ field }) => (
          <FormGroup
            label="Subheader"
            helperText={errors.metadata?.testimonials?.subheader?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <View>
        <Text marginX="xs" marginBottom="xs" text="Testimonials" />
        <QuestionList>
          {testimonials.fields.map((item, index) => (
            <Question key={item.key}>
              <View flex={1}>
                <Controller
                  control={control}
                  name={`metadata.testimonials.items.${index}.quote`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      placeholder="Quote"
                      numberOfLines={3}
                      wrapStyle={{
                        flex: 1,
                        backgroundColor: theme.background,
                        borderColor: theme.border,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        borderBottomWidth: 0
                      }}
                      autoGrow
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`metadata.testimonials.items.${index}.name`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Name"
                      wrapStyle={{
                        flex: 1,
                        backgroundColor: theme.background,
                        borderColor: theme.border,
                        borderRadius: 0,
                        borderBottomWidth: 0
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`metadata.testimonials.items.${index}.headline`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Headline"
                      wrapStyle={{
                        flex: 1,
                        backgroundColor: theme.background,
                        borderColor: theme.border,
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0
                      }}
                    />
                  )}
                />
              </View>
              <DeleteButton icon="trash" onPress={() => testimonials.remove(index)} skipTracking />
            </Question>
          ))}
          <View flexDirection="row">
            <SidebarButton
              icon="add"
              text="Add"
              onPress={() =>
                testimonials.append({
                  name: '' as TranslatedText,
                  headline: '' as TranslatedText,
                  quote: '' as TranslatedText
                })
              }
              skipTracking={true as any}
              action={undefined as any}
            />
            <View flex={1} />
          </View>
        </QuestionList>
      </View>
    </>
  )

  const renderAbout = () => (
    <>
      <Controller
        control={control}
        name="metadata.about.header"
        render={({ field }) => (
          <FormGroup label="Header" helperText={errors.metadata?.about?.header?.message} style={{ marginBottom: 0 }}>
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.about.subheader"
        render={({ field }) => (
          <FormGroup
            label="Subheader"
            helperText={errors.metadata?.about?.subheader?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <View>
        <Text marginX="xs" marginBottom="xs" text="Team" />
        {people.fields.length > 0 && (
          <List>
            {people.fields.map(person => {
              const name = compact([person.firstName, person.lastName].map(part => part?.trim())).join(
                ' '
              ) as TranslatedText

              return (
                <ListItem
                  key={person.key}
                  className="hover-arrow"
                  onPress={() => {
                    setActivePerson({ ...person })
                    setIsPersonDialogOpen(true)
                  }}
                >
                  <ListItemLabel rawText={name} />
                  <Arrow dangerouslySetInnerHTML={{ __html: hoverArrow(12, 8) }} />
                </ListItem>
              )
            })}
          </List>
        )}
      </View>
    </>
  )

  const renderFaq = () => (
    <>
      <Controller
        control={control}
        name="metadata.faq.header"
        render={({ field }) => (
          <FormGroup label="Header" helperText={errors.metadata?.faq?.header?.message} style={{ marginBottom: 0 }}>
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.faq.subheader"
        render={({ field }) => (
          <FormGroup
            label="Subheader"
            helperText={errors.metadata?.faq?.subheader?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <View>
        <Text marginX="xs" marginBottom="xs" text="Questions" />
        <QuestionList>
          {faq.fields.map((question, index) => (
            <Question key={question.key}>
              <View flex={1}>
                <Controller
                  control={control}
                  name={`metadata.faq.questions.${index}.question`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Question"
                      wrapStyle={{
                        flex: 1,
                        backgroundColor: theme.background,
                        borderColor: theme.border,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        borderBottomWidth: 0
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`metadata.faq.questions.${index}.answer`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      placeholder="Answer"
                      numberOfLines={3}
                      wrapStyle={{
                        flex: 1,
                        backgroundColor: theme.background,
                        borderColor: theme.border,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0
                      }}
                      autoGrow
                    />
                  )}
                />
              </View>
              <DeleteButton icon="trash" onPress={() => faq.remove(index)} skipTracking />
            </Question>
          ))}
          <View flexDirection="row">
            <SidebarButton
              icon="add"
              text="Add"
              onPress={() => faq.append({ question: '' as TranslatedText, answer: '' as TranslatedText })}
              skipTracking={true as any}
              action={undefined as any}
            />
            <View flex={1} />
          </View>
        </QuestionList>
      </View>
    </>
  )

  const pendingMemberships = profile.memberships.filter(membership => membership.state === 'Pending')

  const recurringProducts = products.fields.filter(product => product.isRecurring)
  const nonRecurringProducts = products.fields.filter(product => !product.isRecurring)

  const renderPlans = () => (
    <>
      <Controller
        control={control}
        name="metadata.plans.header"
        render={({ field }) => (
          <FormGroup label="Header" helperText={errors.metadata?.plans?.header?.message} style={{ marginBottom: 0 }}>
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.plans.subheader"
        render={({ field }) => (
          <FormGroup
            label="Subheader"
            helperText={errors.metadata?.plans?.subheader?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <ListWrap>
        <Text marginX="xs" text="Plans" />
        {recurringProducts.length > 0 && (
          <List>
            {recurringProducts.map(product => (
              <ListItem
                key={product.key}
                className="hover-arrow"
                onPress={() => {
                  setActiveProduct({ ...product })
                  setIsProductDialogOpen(true)
                }}
              >
                <ListItemLabel rawText={product.name} />
                <Arrow dangerouslySetInnerHTML={{ __html: hoverArrow(12, 8) }} />
              </ListItem>
            ))}
          </List>
        )}
        <View flexDirection="row">
          <SidebarButton
            icon="add"
            text="Add"
            onPress={() => {
              setActiveProduct({
                marketProfileId: profile.id,
                name: '' as TranslatedText,
                description: '' as TranslatedText,
                features: [],
                estimateMin: null,
                estimateMax: null,
                price: null,
                currency: profile.currency,
                isRecurring: true
              })
              setIsProductDialogOpen(true)
            }}
            skipTracking={true as any}
            action={undefined as any}
          />
          <View flex={1} />
        </View>
      </ListWrap>
    </>
  )

  const renderProducts = () => (
    <>
      <Controller
        control={control}
        name="metadata.products.header"
        render={({ field }) => (
          <FormGroup label="Header" helperText={errors.metadata?.products?.header?.message} style={{ marginBottom: 0 }}>
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <Controller
        control={control}
        name="metadata.products.subheader"
        render={({ field }) => (
          <FormGroup
            label="Subheader"
            helperText={errors.metadata?.products?.subheader?.message}
            style={{ marginBottom: 0 }}
          >
            <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
          </FormGroup>
        )}
      />
      <ListWrap>
        <Text marginX="xs" text="Products" />
        {nonRecurringProducts.length > 0 && (
          <List>
            {nonRecurringProducts.map(product => (
              <ListItem
                key={product.key}
                className="hover-arrow"
                onPress={() => {
                  setActiveProduct({ ...product })
                  setIsProductDialogOpen(true)
                }}
              >
                <ListItemLabel rawText={product.name} />
                <Arrow dangerouslySetInnerHTML={{ __html: hoverArrow(12, 8) }} />
              </ListItem>
            ))}
          </List>
        )}
        <View flexDirection="row">
          <SidebarButton
            icon="add"
            text="Add"
            onPress={() => {
              setActiveProduct({
                marketProfileId: profile.id,
                name: '' as TranslatedText,
                description: '' as TranslatedText,
                features: [],
                estimateMin: null,
                estimateMax: null,
                price: null,
                currency: null,
                isRecurring: false
              })
              setIsProductDialogOpen(true)
            }}
            skipTracking={true as any}
            action={undefined as any}
          />
          <View flex={1} />
        </View>
      </ListWrap>
    </>
  )

  const getPaymentsButton = () => {
    const state = profile.stripeState

    if (
      !state ||
      state === 'Pending' ||
      state === 'Incomplete' ||
      state === 'ChargesDisabled' ||
      state === 'PayoutsDisabled'
    ) {
      return (
        <Button
          text={!state || state === 'Pending' ? 'Setup' : 'Continue setup'}
          onPress={async () => {
            if (!state || state === 'Pending') {
              setIsAccountDialogOpen(true)
            }

            if (state === 'Incomplete') {
              const results = await createStripeAccountOnboardingLink({ account: null })

              if (results.success) {
                window.location.href = results.onboardingLink
              } else {
                toast.failure()
              }
            }
          }}
          loading={isCreatingAccountOnboardingLink}
          disabled={isCreatingAccountOnboardingLink}
          skipTracking
        />
      )
    }

    if (state === 'Active') {
      return (
        <Button
          text="Open dashboard"
          onPress={async () => {
            const results = await createStripeAccountLoginLink({})

            if (results.success) {
              openURL(results.loginLink)
            } else {
              toast.failure()
            }
          }}
          loading={isCreatingAccountLoginLink}
          disabled={isCreatingAccountLoginLink}
          skipTracking
        />
      )
    }

    throw new Error(`Unexpected stripe state: ${state}`)
  }

  const renderGeneral = () => {
    return (
      <>
        <Controller
          control={control}
          name="companyName"
          rules={{ required: { value: true, message: t('This field is required') } }}
          render={({ field }) => (
            <FormGroup label="Company name" helperText={errors.companyName?.message} style={{ marginBottom: 0 }}>
              <Input {...field} wrapStyle={{ backgroundColor: theme.background, borderColor: theme.border }} />
            </FormGroup>
          )}
        />
        <GeneralMenuItem>
          <Icon icon="earth" size={24} />
          <MenuItemText text="Website" />
          {isEditingUsername ? (
            <Controller
              control={control}
              name="username"
              render={({ field }) => (
                <View flexDirection="row" alignItems="center" paddingRight={12}>
                  <UsernameInput
                    {...field}
                    wrapStyle={{ width: 200, backgroundColor: 'transparent', borderWidth: 0, borderRadius: 0 }}
                  />
                  <MenuItemSecondaryText rawText={'.weebin.com' as TranslatedText} />
                </View>
              )}
            />
          ) : (
            <>
              {!!profile.username && (
                <MenuItemLink
                  href={`https://${encodeToken(profile.username)}.weebin.com`}
                  onPress={e => {
                    e.preventDefault()
                    openURL(`https://${encodeToken(profile.username)}.weebin.com`)
                  }}
                  rawText={`${profile.username}.weebin.com` as TranslatedText}
                />
              )}
              <Button text="Edit" onPress={() => setIsEditingUsername(true)} skipTracking />
            </>
          )}
        </GeneralMenuItem>
        <GeneralMenuItem>
          <Icon icon="time-outline" size={24} />
          <MenuItemText text="Hourly rate" />
          {isEditingHourlyRate ? (
            <View flexDirection="row" alignItems="center">
              <Controller
                control={control}
                name="hourlyRate"
                render={({ field }) => (
                  <MenuItemInput
                    {...field}
                    keyboardType="numeric"
                    wrapStyle={{ width: 100, backgroundColor: 'transparent', borderWidth: 0, borderRadius: 0 }}
                  />
                )}
              />
              <Controller
                control={control}
                name="currency"
                render={({ field: { value, onChange, ...field } }) => (
                  <Select
                    {...field}
                    options={options.currency}
                    value={options.currency.find(option => option.value === value) ?? null}
                    onChange={value => onChange(value?.value)}
                  />
                )}
              />
            </View>
          ) : (
            <>
              <MenuItemSecondaryText
                rawText={t('{{amount}} / hour', {
                  amount: formatCurrency(locale, profile.currency, profile.hourlyRate, true)
                })}
              />
              <Button text="Edit" onPress={() => setIsEditingHourlyRate(true)} skipTracking />
            </>
          )}
        </GeneralMenuItem>
        <GeneralMenuItem>
          <Icon icon="cash-outline" size={24} />
          <MenuItemText text={profile.stripeState === 'Active' ? 'Payments' : 'Accept payment'} />
          <Tooltip text="10% flat fee + credit card processing" delay={100}>
            <Icon icon="information-circle" size={20} />
          </Tooltip>
          {getPaymentsButton()}
        </GeneralMenuItem>
        <GeneralMenuItem>
          <Icon icon="calendar-outline" size={24} />
          <MenuItemText text="Calendar booking" />
          <Tooltip text="Let people schedule a call with you." delay={100}>
            <Icon icon="information-circle" size={20} />
          </Tooltip>
          <Button
            text={profile.calendarBookingUrl ? 'Edit' : 'Setup'}
            onPress={() => {
              setActiveIntegration('CalendarBooking')
              setIsIntegrationDialogOpen(true)
            }}
            skipTracking
          />
        </GeneralMenuItem>
        <GeneralMenuItem>
          <Icon icon="radio-outline" size={24} />
          <MenuItemText text="Referral commission" />
          <Tooltip
            text="Have others promote your products and services in exchange for a commission on each sale."
            delay={100}
          >
            <Icon icon="information-circle" size={20} />
          </Tooltip>
          <Controller
            control={control}
            name="affiliateRate"
            rules={{ required: { value: true, message: t('This field is required') } }}
            render={({ field: { value, onChange, ...field } }) => (
              <Select
                {...field}
                options={options.commission}
                value={options.commission.find(option => option.value === value) ?? null}
                onChange={value => onChange(value?.value)}
              />
            )}
          />
        </GeneralMenuItem>
        <View>
          <CofounderMenuItem>
            <Icon icon="people-circle-outline" size={24} />
            <MenuItemText text="Co-Founder" />
            <Tooltip
              text="Connect with potential co-founders to jointly develop and grow your startup, sharing responsibilities and rewards."
              delay={100}
            >
              <Icon icon="information-circle" size={20} />
            </Tooltip>
            <Controller
              control={control}
              name="cofounderState"
              rules={{ required: { value: true, message: t('This field is required') } }}
              render={({ field: { value, onChange, ...field } }) => (
                <Select
                  {...field}
                  options={options.cofounder}
                  value={options.cofounder.find(option => option.value === value) ?? null}
                  onChange={value => onChange(value?.value)}
                />
              )}
            />
          </CofounderMenuItem>
          <Controller
            control={control}
            name="cofounderNote"
            render={({ field }) => (
              <Input
                type="textarea"
                placeholder="Add a comment"
                autoGrow
                {...field}
                wrapStyle={{
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0
                }}
              />
            )}
          />
        </View>
      </>
    )
  }

  const renderMain = () => {
    if (isLoadingUser || isLoadingMarketState) {
      return <Loader />
    }

    if (!profile.id) {
      return null
    }

    return (
      <MainBody>
        <Profile
          profile={profile}
          variant="Launchpad"
          isSidebarOpen={canShowSidebar && isSidebarOpen}
          handleEditProduct={product => {
            setActiveProduct({ ...product })
            setIsProductDialogOpen(true)
          }}
          handleBuyProduct={() => {
            if (!profile.stripeState || profile.stripeState === 'Pending') {
              setIsAccountDialogOpen(true)
              return false
            }

            if (profile.stripeState !== 'Active') {
              history.push(`/launchpad/${encodeToken(marketProfileId)}`)
              setIsSidebarOpen(true)
              return false
            }

            if (profile.state !== 'Live') {
              history.push(`/launchpad/${encodeToken(marketProfileId)}`)
              setIsSidebarOpen(true)
              return false
            }

            // continue with showing checkout
            return true
          }}
          handleCalendarBooking={() => {
            if (!profile.calendarBookingUrl) {
              setActiveIntegration('CalendarBooking')
              setIsIntegrationDialogOpen(true)
              return false
            }

            // continue with showing booking screen
            return true
          }}
        />
      </MainBody>
    )
  }

  const canShowSidebar = !isLoadingUser && !isLoadingMarketState

  const onMenuItemPress = (viewId: LaunchpadView) => () => {
    const target = document.getElementById(viewId)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
  }

  if (profile.state === 'Pending' || profile.state === 'Generating') {
    return (
      <Wrap>
        <View flex={1} justifyContent="center" alignItems="center">
          <Loader estimatedSeconds={4 * 60} size="giant" inline />
          <Text text="Generating your site..." marginTop="m" fontSize={16} />
        </View>
      </Wrap>
    )
  }

  return (
    <Wrap>
      <Sidebar $visible={canShowSidebar && isSidebarOpen}>
        <SidebarInner>
          <SidebarMenu>
            <Button
              text="General"
              to={`/launchpad/${encodeToken(marketProfileId)}`}
              onPress={onMenuItemPress('hero')}
              primary={!view || view === 'general'}
              skipTracking
            />
            <Button
              text="Banner"
              to={`/launchpad/${encodeToken(marketProfileId)}/hero`}
              onPress={onMenuItemPress('hero')}
              primary={view === 'hero'}
              skipTracking
            />
            <Button
              text="Deliverables"
              to={`/launchpad/${encodeToken(marketProfileId)}/deliverables`}
              onPress={onMenuItemPress('deliverables')}
              primary={view === 'deliverables'}
              skipTracking
            />
            <Button
              text="Team"
              to={`/launchpad/${encodeToken(marketProfileId)}/about`}
              onPress={onMenuItemPress('about')}
              primary={view === 'about'}
              skipTracking
            />
            <Button
              text="Testimonials"
              to={`/launchpad/${encodeToken(marketProfileId)}/testimonials`}
              onPress={onMenuItemPress('testimonials')}
              primary={view === 'testimonials'}
              skipTracking
            />
            <Button
              text="Plans"
              to={`/launchpad/${encodeToken(marketProfileId)}/plans`}
              onPress={onMenuItemPress('plans')}
              primary={view === 'plans'}
              skipTracking
            />
            <Button
              text="Products"
              to={`/launchpad/${encodeToken(marketProfileId)}/products`}
              onPress={onMenuItemPress('products')}
              primary={view === 'products'}
              skipTracking
            />
            <Button
              text="FAQ"
              to={`/launchpad/${encodeToken(marketProfileId)}/faq`}
              onPress={onMenuItemPress('faq')}
              primary={view === 'faq'}
              skipTracking
            />
          </SidebarMenu>
          <SidebarBody>
            {(view === 'general' || !view) && renderGeneral()}
            {view === 'hero' && renderHero()}
            {view === 'deliverables' && renderDeliverables()}
            {view === 'about' && renderAbout()}
            {view === 'testimonials' && renderTestimonials()}
            {view === 'faq' && renderFaq()}
            {view === 'plans' && renderPlans()}
            {view === 'products' && renderProducts()}
            {/*<Controller
            control={control}
            name="businessType"
            render={({ field: { value, onChange } }) => (
              <SegmentedButtons>
                <SegmentedButton
                  text="Individual"
                  active={value === 'Individual'}
                  $active={value === 'Individual'}
                  onPress={() => onChange('Individual')}
                />
                <SegmentedButton
                  text="Company"
                  active={value === 'Company'}
                  $active={value === 'Company'}
                  onPress={() => onChange('Company')}
                />
              </SegmentedButtons>
            )}
            />*/}
          </SidebarBody>
          {!!changes && (
            <SidebarFooter>
              <Button
                text="Save"
                onPress={async () => {
                  const results = await updateMarketProfile(changes)

                  if (results.success) {
                    queryClient.setQueryData(marketStateQueryKey, results)
                    setIsEditingHourlyRate(false)
                    setIsEditingUsername(false)
                    toast.success('Changes saved')
                  } else {
                    toast.failure()
                  }
                }}
                loading={isUpdatingProfile}
                disabled={isUpdatingProfile}
                flex={1}
                primary
                large
                skipTracking
              />
            </SidebarFooter>
          )}
        </SidebarInner>
      </Sidebar>
      <Main>
        {canShowSidebar && (
          <ToggleSidebarButton
            icon={isSidebarOpen ? 'chevron-back' : 'chevron-forward'}
            onPress={() => setIsSidebarOpen(isOpen => !isOpen)}
            skipTracking
          />
        )}
        {renderMain()}
      </Main>
      {canShowSidebar && (
        <DashboardMenu>
          <Button
            text="Dashboard"
            onPress={() => setIsSidebarOpen(true)}
            active={isSidebarOpen}
            flex={1}
            borderTopRightRadius={0}
            borderBottomRightRadius={0}
            skipTracking
          />
          <Button
            text="Website"
            onPress={() => setIsSidebarOpen(false)}
            active={!isSidebarOpen}
            flex={1}
            borderTopLeftRadius={0}
            borderBottomLeftRadius={0}
            skipTracking
          />
        </DashboardMenu>
      )}
      <AccountDialog open={isAccountDialogOpen} onClose={() => setIsAccountDialogOpen(false)} />
      {!!activeIntegration && (
        <IntegrationDialog
          open={isIntegrationDialogOpen}
          onClose={() => setIsIntegrationDialogOpen(false)}
          profile={profile}
          type={activeIntegration}
        />
      )}
      {!!activePerson && (
        <PersonDialog
          open={isPersonDialogOpen}
          onClose={() => setIsPersonDialogOpen(false)}
          people={people}
          person={activePerson}
        />
      )}
      {!!activeProduct && (
        <ProductDialog
          open={isProductDialogOpen}
          onClose={() => setIsProductDialogOpen(false)}
          profile={profile}
          products={products}
          product={activeProduct}
        />
      )}
    </Wrap>
  )
}
